import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Box, 
  DollarSign, 
  Calendar, 
  Truck, 
  FileText, 
  Save, 
  XCircle, 
  Package, 
  Tag, 
  Plus, 
  Trash2, 
  Search,
  X,
  QrCode,
  AlertCircle
} from 'lucide-react';
import { productsService } from '../services/productsService';
import { suppliersService } from '../services/suppliersService';

const EntryFormMulti = ({ onSave, onCancel }) => {
  // Estado principal del formulario
  const [entry, setEntry] = useState({
    supplier_id: '',
    entry_date: new Date().toISOString().split('T')[0], // Fecha actual formato YYYY-MM-DD
    invoice_number: '',
    notes: '',
    products: [] // Array de productos
  });

  // Estados para manejar selección de productos y proveedores
  const [allProducts, setAllProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [showProductSelector, setShowProductSelector] = useState(false);
  
  // Estados para escaneo de código de barras
  const [barcodeBuffer, setBarcodeBuffer] = useState('');
  const [lastKeypressTime, setLastKeypressTime] = useState(0);
  const [barcodeNotification, setBarcodeNotification] = useState({ 
    show: false, 
    message: '', 
    type: 'success',
    details: '' 
  });
  // Mapa para búsqueda rápida de productos por código
  const [productSearchMap, setProductSearchMap] = useState({});
  
  // Carga de productos y proveedores
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Cargar todos los productos (sin paginación)
        const productsResponse = await productsService.getAllUnpaginated();
        console.log('Productos cargados:', productsResponse);
        setAllProducts(productsResponse || []);
        setFilteredProducts(productsResponse || []);
        
        // Crear un mapa de búsqueda para acceso rápido por códigos
        const searchMap = {};
        (productsResponse || []).forEach(product => {
          // Indexar por barcode (código de barras)
          if (product.barcode) {
            const normalizedBarcode = product.barcode.trim().toLowerCase();
            searchMap[normalizedBarcode] = product;
            
            // Versión sin caracteres especiales
            const cleanBarcode = normalizedBarcode.replace(/[^a-z0-9]/g, '');
            if (cleanBarcode && cleanBarcode.length > 3) {
              searchMap[cleanBarcode] = product;
            }
            
            // Versión con guiones en lugar de comillas
            const dashedBarcode = normalizedBarcode.replace(/'/g, '-');
            if (dashedBarcode !== normalizedBarcode) {
              searchMap[dashedBarcode] = product;
            }
          }
          
          // Indexar por SKU
          if (product.sku) {
            const normalizedSku = product.sku.trim().toLowerCase();
            searchMap[normalizedSku] = product;
            
            // Versión sin caracteres especiales
            const cleanSku = normalizedSku.replace(/[^a-z0-9]/g, '');
            if (cleanSku && cleanSku.length > 3) {
              searchMap[cleanSku] = product;
            }
          }
          
          // Indexar por code (código interno)
          if (product.code) {
            const normalizedCode = product.code.trim().toLowerCase();
            searchMap[normalizedCode] = product;
            
            // Versión sin caracteres especiales
            const cleanCode = normalizedCode.replace(/[^a-z0-9]/g, '');
            if (cleanCode && cleanCode.length > 3) {
              searchMap[cleanCode] = product;
            }
          }
        });
        
        console.log('🔍 Mapa de búsqueda creado con', Object.keys(searchMap).length, 'entradas');
        setProductSearchMap(searchMap);
        
        // Cargar todos los proveedores
        const suppliersResponse = await suppliersService.getAll();
        console.log('Proveedores cargados:', suppliersResponse);
        setSuppliers(suppliersResponse?.data || []);
        
      } catch (error) {
        console.error('Error cargando datos:', error);
      } finally {
        setLoading(false);
      }
    };
    
    fetchData();
  }, []);
  
  // Filtrar productos basado en el término de búsqueda
  useEffect(() => {
    if (!searchTerm.trim()) {
      setFilteredProducts(allProducts);
      return;
    }
    
    const filtered = allProducts.filter(product => 
      product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      product.sku.toLowerCase().includes(searchTerm.toLowerCase()) ||
      String(product.id).includes(searchTerm)
    );
    
    setFilteredProducts(filtered);
  }, [searchTerm, allProducts]);
  
  // Registrar y desregistrar evento de escucha de teclado para el escáner de códigos de barras
  useEffect(() => {
    // Función para capturar eventos de teclado en todo el documento
    const handleGlobalKeyDown = (event) => {
      // Solo procesar si no estamos en un campo de entrada
      const activeElement = document.activeElement;
      const isInputActive = activeElement.tagName === 'INPUT' || 
                           activeElement.tagName === 'TEXTAREA' || 
                           activeElement.isContentEditable;
      
      // Si estamos en un campo de entrada y no es un Enter, permitir el comportamiento normal
      if (isInputActive && event.key !== 'Enter') {
        return;
      }
      
      // Si se presiona Enter en un campo de entrada, primero procesamos normalmente
      // pero también queremos capturarlo para el código de barras
      if (isInputActive && event.key === 'Enter') {
        // Permitir que el evento se propague para su comportamiento normal
        // pero también lo procesamos para el código de barras
        handleKeyDown(event);
        return;
      }
      
      // Para cualquier otra tecla fuera de campos de entrada, procesarla para el código de barras
      handleKeyDown(event);
    };
    
    // Registrar el evento
    document.addEventListener('keydown', handleGlobalKeyDown);
    
    // Limpieza al desmontar
    return () => {
      document.removeEventListener('keydown', handleGlobalKeyDown);
    };
  }, [barcodeBuffer, lastKeypressTime]); // Dependencias necesarias para que el efecto se actualice
  
  // Calcular el costo total de la entrada
  const getTotalCost = () => {
    return entry.products.reduce((sum, item) => sum + (item.quantity * item.unit_cost), 0);
  };
  
  // Función para normalizar códigos específicos
  const normalizeSpecificCodes = (code) => {
    // Lista de patrones conocidos y sus normalizaciones
    const knownPatterns = [
      // Formatos de aceites y lubricantes que pueden contener comillas
      { pattern: /ACE'(\d+)W(\d+)/i, replacement: 'ACE-$1W$2' }, // Ejemplo: ACE'10W40 -> ACE-10W40
      { pattern: /ACE'(\d+[A-Z]\d+)/i, replacement: 'ACE-$1' }, // Más genérico para otros formatos de ACE
      { pattern: /'(\d+W\d+)/i, replacement: '-$1' }, // Ejemplo: '10W40 -> -10W40
      
      // Patrones genéricos para manejar códigos con formato [texto]'[números/letras]
      { pattern: /(\w+)'(\w+)/g, replacement: '$1-$2' }, // Patrón general: TEXTO'TEXTO -> TEXTO-TEXTO
      
  // Reemplazar comillas simples por guiones (caso común en escáneres)
  { pattern: /'([A-Za-z0-9]+)'/g, replacement: '-$1-' }, // Ejemplo: 'ABC123' -> -ABC123-
  { pattern: /'([A-Za-z0-9]+)/g, replacement: '-$1' },   // Ejemplo: 'ABC123 -> -ABC123
  { pattern: /([A-Za-z0-9]+)'/g, replacement: '$1-' },   // Ejemplo: ABC123' -> ABC123-
      
  // Manejar comillas simples sueltas
  { pattern: /'/g, replacement: '-' },  // Reemplazar todas las comillas simples por guiones
  // Manejar comas que algunos escáneres insertan en lugar de guiones
  { pattern: /,/g, replacement: '-' },  // Reemplazar comas por guiones
      
      // Limpiar guiones múltiples consecutivos que pudieran generarse
      { pattern: /-{2,}/g, replacement: '-' }, // Ejemplo: --- -> -
      
      // Quitar espacios excesivos que pudieran confundir la búsqueda
      { pattern: /\s+/g, replacement: ' ' },
    ];
    
    // Aplicar cada patrón de normalización
    let normalizedCode = code;
    knownPatterns.forEach(({ pattern, replacement }) => {
      normalizedCode = normalizedCode.replace(pattern, replacement);
    });
    
    return normalizedCode;
  };
  
  // Función para procesar el código de barras completo
  const processBarcodeInput = () => {
    // Constantes para configuración
    const MIN_BARCODE_LENGTH = 4;
    
    // Lista de teclas especiales que no deberían considerarse como códigos de barras válidos
    const specialKeys = ['Backspace', 'Delete', 'Enter', 'Tab', 'Escape', 'Home', 'End', 
                        'PageUp', 'PageDown', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight',
                        'F1', 'F2', 'F3', 'F4', 'F5', 'F6', 'F7', 'F8', 'F9', 'F10', 'F11', 'F12'];
    
    // Verificar si el buffer contiene solo una tecla especial (rápida verificación)
    if (specialKeys.includes(barcodeBuffer)) {
      console.log('🔄 Ignorando tecla especial:', barcodeBuffer);
      setBarcodeBuffer('');
      return;
    }
    
    if (barcodeBuffer.length >= MIN_BARCODE_LENGTH) {
      // Tiempo de inicio para medir rendimiento
      const startTime = performance.now();
      
      // Limpiamos el código de barras para evitar caracteres problemáticos
      const sanitizedBarcode = barcodeBuffer.replace(/['"\\]/g, ''); // Eliminar comillas y barras invertidas
      
      // Aplicar normalización específica para formatos conocidos
      const normalizedBarcode = normalizeSpecificCodes(barcodeBuffer);
      
      console.log('🔍 Código de barras escaneado (original):', barcodeBuffer);
      
      // Buscar primero en el mapa de búsqueda rápida (esto es más rápido)
      let foundProduct = null;
      const searchKeys = [
        barcodeBuffer.trim().toLowerCase(),
        sanitizedBarcode.trim().toLowerCase(),
        normalizedBarcode.trim().toLowerCase(),
        barcodeBuffer.replace(/'/g, '-').trim().toLowerCase(),
        barcodeBuffer.replace(/,/g, '-').trim().toLowerCase(),
        barcodeBuffer.replace(/[^a-zA-Z0-9]/g, '').trim().toLowerCase(),
        sanitizedBarcode.replace(/[^a-zA-Z0-9]/g, '').trim().toLowerCase(),
        normalizedBarcode.replace(/[^a-zA-Z0-9]/g, '').trim().toLowerCase()
      ];
      
      // Intentar encontrar el producto en el mapa de búsqueda (más rápido)
      for (const key of searchKeys) {
        if (productSearchMap[key]) {
          foundProduct = productSearchMap[key];
          console.log('✅ Producto encontrado rápidamente por clave:', key);
          break;
        }
      }
      
      // Si no se encontró en el mapa, intentar el método tradicional
      if (!foundProduct) {
        // Crear un array de posibles formatos para el método tradicional
        const possibleCodes = [
          sanitizedBarcode.trim(),
          normalizedBarcode.trim(),
          barcodeBuffer.trim(),
          barcodeBuffer.replace(/'/g, '-').trim(),
          barcodeBuffer.replace(/[^a-zA-Z0-9]/g, '').trim(),
          sanitizedBarcode.replace(/[^a-zA-Z0-9]/g, '').trim(),
          normalizedBarcode.replace(/[^a-zA-Z0-9]/g, '').trim()
        ];
        
        foundProduct = allProducts.find(product => {
          // Obtener los códigos del producto y normalizarlos
          const productBarcode = product.barcode ? product.barcode.trim() : '';
          const productSku = product.sku ? product.sku.trim() : '';
          const productCode = product.code ? product.code.trim() : '';
          
          // También normalizar los códigos del producto para mayor compatibilidad
          const normalizedBarcode = productBarcode ? normalizeSpecificCodes(productBarcode) : '';
          const normalizedSku = productSku ? normalizeSpecificCodes(productSku) : '';
          const normalizedCode = productCode ? normalizeSpecificCodes(productCode) : '';
          
          // Crear un array con todos los posibles códigos del producto
          const productCodes = [
            productBarcode, productSku, productCode,
            normalizedBarcode, normalizedSku, normalizedCode,
            // Versiones en minúsculas
            productBarcode.toLowerCase(), productSku.toLowerCase(), productCode.toLowerCase(),
            normalizedBarcode.toLowerCase(), normalizedSku.toLowerCase(), normalizedCode.toLowerCase(),
            // Versiones sin caracteres especiales
            productBarcode.replace(/[^a-zA-Z0-9]/g, ''),
            productSku.replace(/[^a-zA-Z0-9]/g, ''),
            productCode.replace(/[^a-zA-Z0-9]/g, '')
          ].filter(code => code !== ''); // Eliminar valores vacíos
          
          // Verificar coincidencias entre los códigos del producto y los posibles formatos del código escaneado
          return possibleCodes.some(scannedCode => {
            if (!scannedCode) return false;
            
            // Comprobar coincidencia exacta o con normalización
            return productCodes.some(productCode => {
              if (!productCode) return false;
              
              // Comprobar coincidencia exacta
              if (productCode === scannedCode) return true;
              
              // Comprobar si el código escaneado está contenido en el código del producto (para casos parciales)
              if (productCode.includes(scannedCode) && scannedCode.length >= 5) return true;
              
              // Comprobar si el código del producto está contenido en el código escaneado (para casos con prefijos/sufijos)
              if (scannedCode.includes(productCode) && productCode.length >= 5) return true;
              
              return false;
            });
          });
        });
      }
      
      // Medir el tiempo que tomó la búsqueda
      const endTime = performance.now();
      const searchTime = (endTime - startTime).toFixed(2);
      console.log(`🕒 Búsqueda completada en ${searchTime}ms`);
      
      if (foundProduct) {
        console.log('✅ Producto encontrado:', foundProduct);
        
        // Agregar el producto a la entrada si no está ya añadido
        const isProductAlreadyAdded = entry.products.some(p => p.product_id === foundProduct.id);
        
        if (isProductAlreadyAdded) {
          // Incrementar la cantidad si el producto ya está en la lista
          setEntry(prev => ({
            ...prev,
            products: prev.products.map(p => 
              p.product_id === foundProduct.id 
                ? { ...p, quantity: p.quantity + 1, total_cost: (p.quantity + 1) * p.unit_cost } 
                : p
            )
          }));
          
          // Mostrar notificación de incremento
          setBarcodeNotification({
            show: true,
            message: `Cantidad incrementada: ${foundProduct.name}`,
            type: 'success',
            details: `SKU: ${foundProduct.sku || ''} | Nuevo total: ${
              entry.products.find(p => p.product_id === foundProduct.id)?.quantity + 1
            }`
          });
        } else {
          // Añadir el producto a la lista
          setEntry(prev => ({
            ...prev,
            products: [
              ...prev.products,
              {
                product_id: foundProduct.id,
                product_name: foundProduct.name,
                product_sku: foundProduct.sku,
                product_unit: foundProduct.unit,
                quantity: 1,
                unit_cost: foundProduct.price || 0,
                total_cost: foundProduct.price || 0
              }
            ]
          }));
          
          // Mostrar notificación de éxito
          setBarcodeNotification({
            show: true,
            message: `Producto agregado: ${foundProduct.name}`,
            type: 'success',
            details: `SKU: ${foundProduct.sku || ''} | Código: ${foundProduct.code || ''}`
          });
        }
        
        // Limpiar la notificación después de un momento
        setTimeout(() => {
          setBarcodeNotification({ show: false, message: '', type: 'success', details: '' });
        }, 3000);
      } else {
        // Construir mensaje con información útil para depuración
        console.log('❌ No se encontró ningún producto:', 
          'Original:', JSON.stringify(barcodeBuffer), 
          'Sanitizado:', JSON.stringify(sanitizedBarcode), 
          'Normalizado:', JSON.stringify(normalizedBarcode));
        
        // Mostrar notificación de error con información útil
        setBarcodeNotification({
          show: true,
          message: `No se encontró producto con código: ${normalizedBarcode}`,
          type: 'error',
          details: `Prueba buscar manualmente con: ${barcodeBuffer}`
        });
        
        // Ocultar la notificación después de un tiempo
        setTimeout(() => {
          setBarcodeNotification({ show: false, message: '', type: 'error', details: '' });
        }, 3000);
      }
    } else if (barcodeBuffer.length > 0) {
      console.log('⚠️ Código demasiado corto, ignorando:', barcodeBuffer);
      
      // Mostrar notificación de código inválido
      setBarcodeNotification({
        show: true,
        message: 'Código demasiado corto para ser válido',
        type: 'warning'
      });
      
      setTimeout(() => {
        setBarcodeNotification(prev => ({ ...prev, show: false }));
      }, 2000);
    }
    
    // Limpiar el buffer para la siguiente lectura
    setBarcodeBuffer('');
  };
  
  // Función para manejar las pulsaciones de teclas (para el escaneo de códigos de barras)
  const handleKeyDown = (event) => {
    // Constantes para configuración
    const MAX_SCAN_GAP = 50; // Tiempo máximo entre pulsaciones para considerarlas parte del mismo escaneo (ms)
    const SCAN_TIMEOUT = 300; // Tiempo máximo para considerar un escaneo completo (ms)
    const MIN_BARCODE_LENGTH = 4; // Longitud mínima del código para considerarlo un código de barras válido
    
    // Ignorar eventos de teclas modificadoras, navegación y teclas especiales
    if (event.ctrlKey || event.altKey || event.metaKey) return;
    if (event.key === 'Shift' || event.key === 'Control' || event.key === 'Alt' || event.key === 'Meta') return;
    if (event.key === 'Tab' || event.key === 'Escape' || event.key === 'ArrowUp' || 
        event.key === 'ArrowDown' || event.key === 'ArrowLeft' || event.key === 'ArrowRight') return;
        
    // Ignorar la tecla Backspace y otras teclas de control para evitar procesarlas como códigos de barras
    if (event.key === 'Backspace' || event.key === 'Delete' || event.key === 'Home' || 
        event.key === 'End' || event.key === 'PageUp' || event.key === 'PageDown' ||
        event.key === 'F1' || event.key === 'F2' || event.key === 'F3' || event.key === 'F4' ||
        event.key === 'F5' || event.key === 'F6' || event.key === 'F7' || event.key === 'F8' ||
        event.key === 'F9' || event.key === 'F10' || event.key === 'F11' || event.key === 'F12') {
      // Solo permitir que Backspace borre caracteres del buffer si hay contenido
      if (event.key === 'Backspace' && barcodeBuffer.length > 0) {
        setBarcodeBuffer(prevBuffer => prevBuffer.slice(0, -1));
        setLastKeypressTime(new Date().getTime());
      }
      return;
    }
    
    const currentTime = new Date().getTime();
    const timeSinceLastKeypress = currentTime - lastKeypressTime;
    
    // Los escáneres de código de barras generalmente envían caracteres muy rápidamente
    if (timeSinceLastKeypress > MAX_SCAN_GAP && barcodeBuffer.length > 0) {
      // Si ha pasado más tiempo, es una nueva lectura o interacción manual
      // Solo resetear si ya teníamos algo en el buffer (podría ser una entrada manual lenta)
      if (timeSinceLastKeypress > SCAN_TIMEOUT) {
        setBarcodeBuffer('');
      }
    }
    
    // Procesar el caracter
    if (event.key === 'Enter') {
      // El Enter típicamente señala el final de un escaneo de código de barras
      event.preventDefault(); // Prevenir la acción predeterminada para no enviar formularios
      
      if (barcodeBuffer.length >= MIN_BARCODE_LENGTH) {
        processBarcodeInput();
      } else if (barcodeBuffer.length > 0) {
        console.log('⚠️ Código demasiado corto, ignorando:', barcodeBuffer);
        setBarcodeBuffer('');
        
        // Mostrar notificación de código inválido
        setBarcodeNotification({
          show: true,
          message: 'Código demasiado corto para ser válido',
          type: 'warning'
        });
        
        setTimeout(() => {
          setBarcodeNotification(prev => ({ ...prev, show: false }));
        }, 2000);
      }
    } else {
      // Acumular el caracter en el buffer usando try-catch para manejar caracteres problemáticos
      try {
        setBarcodeBuffer(prevBuffer => prevBuffer + event.key);
      } catch (error) {
        console.error('Error al añadir caracter al buffer:', error);
        // Si hay un error, intentamos añadir un valor seguro
        setBarcodeBuffer(prevBuffer => prevBuffer + '_');
      }
    }
    
    setLastKeypressTime(currentTime);
  };
  
  // Manejar cambios en los campos del formulario
  const handleChange = (e) => {
    const { name, value } = e.target;
    setEntry(prev => ({
      ...prev,
      [name]: value
    }));
  };
  
  // Agregar un producto a la entrada
  const handleAddProduct = (product) => {
    setEntry(prev => ({
      ...prev,
      products: [
        ...prev.products,
        {
          product_id: product.id,
          product_name: product.name, // Para mostrar en la UI
          product_sku: product.sku,   // Para mostrar en la UI
          product_unit: product.unit, // Para mostrar en la UI
          quantity: 1,
          unit_cost: product.price || 0,
          total_cost: product.price || 0
        }
      ]
    }));
    
    setShowProductSelector(false);
  };
  
  // Eliminar un producto de la entrada
  const handleRemoveProduct = (index) => {
    setEntry(prev => ({
      ...prev,
      products: prev.products.filter((_, i) => i !== index)
    }));
  };
  
  // Actualizar cantidad o costo unitario de un producto
  const handleProductChange = (index, field, value) => {
    setEntry(prev => {
      const updatedProducts = [...prev.products];
      updatedProducts[index] = {
        ...updatedProducts[index],
        [field]: value
      };
      
      // Actualizar el costo total si cambia la cantidad o el costo unitario
      if (field === 'quantity' || field === 'unit_cost') {
        const quantity = field === 'quantity' ? value : updatedProducts[index].quantity;
        const unitCost = field === 'unit_cost' ? value : updatedProducts[index].unit_cost;
        updatedProducts[index].total_cost = quantity * unitCost;
      }
      
      return {
        ...prev,
        products: updatedProducts
      };
    });
  };
  
  // Manejar envío del formulario
  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validación básica
    if (entry.products.length === 0) {
      alert('Debes agregar al menos un producto a la entrada');
      return;
    }
    
    // Validar que todos los productos tengan cantidad y costo válidos
    const invalidProducts = entry.products.filter(
      p => !p.quantity || p.quantity <= 0 || !p.unit_cost || p.unit_cost <= 0
    );
    
    if (invalidProducts.length > 0) {
      alert('Todos los productos deben tener una cantidad y costo unitario válido mayor a 0');
      return;
    }
    
    // Preparar los datos para enviar al backend
    const entryData = {
      supplier_id: entry.supplier_id || null,
      entry_date: entry.entry_date,
      invoice_number: entry.invoice_number || null,
      notes: entry.notes || null,
      products: entry.products.map(p => ({
        product_id: p.product_id,
        quantity: Number(p.quantity),
        unit_cost: Number(p.unit_cost)
      }))
    };
    
    console.log('Datos de entrada a guardar:', entryData);
    onSave(entryData);
  };
  
  // Si está cargando, mostrar spinner
  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }
  
  return (
    <motion.div
      className="bg-white rounded-3xl shadow-2xl p-8 sm:p-10 md:p-12 w-full max-w-4xl mx-auto border border-gray-200"
      initial={{ scale: 0.9, opacity: 0 }}
      animate={{ scale: 1, opacity: 1 }}
      exit={{ scale: 0.9, opacity: 0 }}
      transition={{ type: "spring", stiffness: 100, damping: 10 }}
    >
      {/* Notificación de código de barras */}
      {barcodeNotification.show && (
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -50 }}
          className={`fixed top-4 right-4 z-50 flex items-center p-4 mb-4 rounded-lg ${
            barcodeNotification.type === 'success' 
              ? 'bg-green-50 text-green-800 border border-green-300' 
              : barcodeNotification.type === 'error'
                ? 'bg-red-50 text-red-800 border border-red-300'
                : 'bg-yellow-50 text-yellow-800 border border-yellow-300'
          }`}
          role="alert"
        >
          <div className="flex-shrink-0 w-5 h-5 mr-3">
            {barcodeNotification.type === 'success' ? (
              <QrCode className="w-5 h-5 text-green-700" />
            ) : barcodeNotification.type === 'error' ? (
              <AlertCircle className="w-5 h-5 text-red-700" />
            ) : (
              <AlertCircle className="w-5 h-5 text-yellow-700" />
            )}
          </div>
          <div className="flex flex-col">
            <span className="font-medium">{barcodeNotification.message}</span>
            {barcodeNotification.details && (
              <span className="text-xs opacity-75">{barcodeNotification.details}</span>
            )}
          </div>
        </motion.div>
      )}
      
      <div className="flex flex-col items-center mb-8">
        <motion.div
          className="p-4 bg-gradient-to-br from-blue-500 to-cyan-600 rounded-full shadow-lg mb-4"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: "spring", stiffness: 150, damping: 10, delay: 0.1 }}
        >
          <Package className="w-8 h-8 text-white" />
        </motion.div>
        <h2 className="text-3xl font-bold text-gray-800 mb-2">
          Registrar Nueva Entrada
        </h2>
        <p className="text-gray-500 text-center">
          Introduce los detalles de la nueva entrada y agrega los productos recibidos.
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* Sección de información general */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="col-span-2">
            <label className="block text-gray-700 text-sm font-medium mb-2">
              Proveedor
            </label>
            <div className="relative">
              <Truck className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <select
                name="supplier_id"
                className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-blue-400 focus:border-blue-400 outline-none transition-all duration-200 text-gray-800"
                value={entry.supplier_id}
                onChange={handleChange}
              >
                <option value="">Seleccionar proveedor</option>
                {suppliers.map(supplier => (
                  <option key={supplier.id} value={supplier.id}>
                    {supplier.name}
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-gray-700 text-sm font-medium mb-2">
              Fecha de Entrada
            </label>
            <div className="relative">
              <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="date"
                name="entry_date"
                className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-blue-400 focus:border-blue-400 outline-none transition-all duration-200 text-gray-800"
                value={entry.entry_date}
                onChange={handleChange}
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-gray-700 text-sm font-medium mb-2">
              Número de Factura
            </label>
            <div className="relative">
              <FileText className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                name="invoice_number"
                placeholder="Ej. FACT-2025-001"
                className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-blue-400 focus:border-blue-400 outline-none transition-all duration-200 text-gray-800"
                value={entry.invoice_number}
                onChange={handleChange}
              />
            </div>
          </div>

          <div className="col-span-2">
            <label className="block text-gray-700 text-sm font-medium mb-2">
              Notas
            </label>
            <div className="relative">
              <textarea
                name="notes"
                placeholder="Observaciones o detalles adicionales"
                className="w-full pl-4 pr-4 py-3 rounded-xl border border-gray-300 focus:ring-2 focus:ring-blue-400 focus:border-blue-400 outline-none transition-all duration-200 text-gray-800"
                value={entry.notes}
                onChange={handleChange}
                rows={3}
              />
            </div>
          </div>
        </div>

        {/* Sección de productos */}
        <div className="border-t border-gray-200 pt-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-xl font-bold text-gray-800">
              Productos Recibidos
            </h3>
            <motion.button
              type="button"
              onClick={() => setShowProductSelector(true)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              <Plus className="w-5 h-5" />
              Agregar Producto
            </motion.button>
          </div>

          {entry.products.length === 0 ? (
            <div className="text-center py-10 bg-gray-50 rounded-xl border border-dashed border-gray-300">
              <Package className="w-12 h-12 mx-auto text-gray-400 mb-3" />
              <p className="text-gray-500">No hay productos agregados</p>
              <p className="text-sm text-gray-400 mt-1">Haz clic en "Agregar Producto" para comenzar</p>
            </div>
          ) : (
            <div className="space-y-4">
              {entry.products.map((product, index) => (
                <motion.div
                  key={index}
                  className="bg-white p-4 rounded-xl border border-gray-200 shadow-sm hover:shadow-md transition-shadow"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                    <div className="flex items-center gap-3 flex-grow">
                      <div className="p-2 bg-purple-100 rounded-lg">
                        <Package className="w-5 h-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="font-medium text-gray-800">{product.product_name}</p>
                        <p className="text-sm text-gray-500">SKU: {product.product_sku}</p>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-3 flex-grow">
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">
                          Cantidad
                        </label>
                        <input
                          type="number"
                          min="1"
                          step="1"
                          value={product.quantity}
                          onChange={(e) => handleProductChange(index, 'quantity', Number(e.target.value))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-800"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">
                          Costo Unitario
                        </label>
                        <input
                          type="number"
                          min="0"
                          step="0.01"
                          value={product.unit_cost}
                          onChange={(e) => handleProductChange(index, 'unit_cost', Number(e.target.value))}
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg text-gray-800"
                        />
                      </div>
                      <div>
                        <label className="block text-xs text-gray-500 mb-1">
                          Total
                        </label>
                        <div className="px-3 py-2 bg-gray-100 rounded-lg text-gray-800 font-medium">
                          ${(product.quantity * product.unit_cost).toFixed(2)}
                        </div>
                      </div>
                    </div>

                    <button
                      type="button"
                      onClick={() => handleRemoveProduct(index)}
                      className="text-red-500 hover:text-red-700 p-2 rounded-full hover:bg-red-50 transition-colors"
                    >
                      <Trash2 className="w-5 h-5" />
                    </button>
                  </div>
                </motion.div>
              ))}

              <div className="flex justify-end mt-4">
                <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
                  <div className="text-sm text-blue-600 mb-1">Costo Total de la Entrada:</div>
                  <div className="text-2xl font-bold text-blue-700">
                    ${getTotalCost().toFixed(2)} MXN
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Botones de acción */}
        <div className="flex justify-end gap-4 mt-8 pt-6 border-t border-gray-200">
          <motion.button
            type="button"
            onClick={onCancel}
            className="flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-lg bg-gray-200 text-gray-700 shadow-md hover:shadow-lg transform hover:scale-105 transition-all duration-300"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
          >
            <XCircle className="w-5 h-5" />
            Cancelar
          </motion.button>
          <motion.button
            type="submit"
            className="flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-lg bg-gradient-to-r from-blue-600 to-cyan-600 text-white shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.98 }}
            disabled={entry.products.length === 0}
          >
            <Save className="w-5 h-5" />
            Registrar Entrada
          </motion.button>
        </div>
      </form>

      {/* Modal para seleccionar productos */}
      <AnimatePresence>
        {showProductSelector && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
          >
            <motion.div
              className="bg-white rounded-2xl shadow-2xl p-6 w-full max-w-3xl"
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
            >
              <div className="flex justify-between items-center mb-6">
                <h3 className="text-xl font-bold text-gray-800">
                  Seleccionar Producto
                </h3>
                <button
                  onClick={() => setShowProductSelector(false)}
                  className="text-gray-500 hover:text-gray-700"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="mb-4 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Buscar por nombre o SKU"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg"
                />
              </div>

              <div className="max-h-[60vh] overflow-y-auto pr-2">
                {filteredProducts.length === 0 ? (
                  <div className="text-center py-8 text-gray-500">
                    <Package className="w-12 h-12 mx-auto mb-3 text-gray-400" />
                    <p>No se encontraron productos</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 gap-3">
                    {filteredProducts.map(product => (
                      <motion.div
                        key={product.id}
                        className="p-3 border border-gray-200 rounded-xl hover:bg-blue-50 hover:border-blue-300 cursor-pointer transition-colors"
                        whileHover={{ scale: 1.01 }}
                        onClick={() => handleAddProduct(product)}
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <div className="p-2 bg-blue-100 rounded-lg">
                              <Package className="w-5 h-5 text-blue-600" />
                            </div>
                            <div>
                              <p className="font-medium text-gray-800">{product.name}</p>
                              <div className="flex items-center gap-3 text-sm text-gray-500">
                                <span>SKU: {product.sku}</span>
                                <span>•</span>
                                <span>Stock: {product.current_stock || 0} {product.unit}</span>
                              </div>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-medium text-green-600">
                              ${product.price ? parseFloat(product.price).toFixed(2) : '0.00'}
                            </p>
                          </div>
                        </div>
                      </motion.div>
                    ))}
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default EntryFormMulti;
